import CryptoJS from 'crypto-js';

/**
 * Generador de JWT IDs (JTI) criptográficamente seguros.
 *
 * Compatible con el backend PHP JtiGenerator.
 *
 * Formato: {prefix}-{timestamp}-{nonce}-{hash}
 * - prefix: Prefijo personalizable (auth, payment, refresh, etc.)
 * - timestamp: Unix timestamp en segundos (10 dígitos)
 * - nonce: 32 caracteres hexadecimales (128 bits de entropía)
 * - hash: 16 primeros caracteres de SHA256 (128 bits)
 *
 * Entropía Total: ~416 bits
 *
 * @example
 * const jti = JtiGenerator.generate();
 * // Output: auth-1707912000-a3f2c8d9e1b4f5a6c7d8e9f0a1b2c3d4-5f8a9b2c3d4e5f6a
 */
export class JtiGenerator {
  static generate(prefix = 'auth', timestamp) {
    const ts = timestamp != null ? timestamp : Math.floor(Date.now() / 1000);

    const source1 = ts.toString();

    const randomBytes = new Uint8Array(16);
    crypto.getRandomValues(randomBytes);
    const source2 = Array.from(randomBytes, (b) => b.toString(16).padStart(2, '0')).join('');

    const source3 = this.generateUUID();

    const source4 = (Date.now() + performance.now()).toString();

    const extraRandomBytes = new Uint8Array(8);
    crypto.getRandomValues(extraRandomBytes);
    const source5 = Array.from(extraRandomBytes, (b) => b.toString(16).padStart(2, '0')).join('');

    const entropyString = `${source1}${source2}${source3}${source4}${source5}`;

    const nonce = source2;

    const hashWordArray = CryptoJS.SHA256(entropyString);
    const fullHash = hashWordArray.toString(CryptoJS.enc.Hex);

    const hash = fullHash.substring(0, 16);

    return `${prefix}-${ts}-${nonce}-${hash}`;
  }

  static generateUUID() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

    return [
      hex.substring(0, 8),
      hex.substring(8, 12),
      hex.substring(12, 16),
      hex.substring(16, 20),
      hex.substring(20, 32),
    ].join('-');
  }

  static isValid(jti, expectedPrefix = 'auth') {
    if (!jti || typeof jti !== 'string') {
      return false;
    }

    const parts = jti.split('-');

    if (parts.length !== 4) {
      return false;
    }

    const [prefix, timestamp, nonce, hash] = parts;

    if (expectedPrefix && prefix !== expectedPrefix) {
      return false;
    }

    if (!/^\d{10}$/.test(timestamp)) {
      return false;
    }

    if (!/^[a-f0-9]{32}$/i.test(nonce)) {
      return false;
    }

    if (!/^[a-f0-9]{16}$/i.test(hash)) {
      return false;
    }

    return true;
  }

  static extractTimestamp(jti) {
    if (!this.isValid(jti, '')) {
      return null;
    }

    const parts = jti.split('-');
    const timestamp = parseInt(parts[1], 10);

    return isNaN(timestamp) ? null : timestamp;
  }

  static isRecent(jti, maxAgeSeconds = 3600) {
    const timestamp = this.extractTimestamp(jti);

    if (timestamp === null) {
      return false;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const age = currentTime - timestamp;

    return age >= 0 && age <= maxAgeSeconds;
  }

  static getDebugInfo(jti) {
    if (!this.isValid(jti, '')) {
      return {
        valid: false,
        error: 'Formato de JTI inválido',
      };
    }

    const parts = jti.split('-');
    const timestamp = parseInt(parts[1], 10);
    const currentTime = Math.floor(Date.now() / 1000);
    const age = currentTime - timestamp;

    return {
      valid: true,
      jti,
      prefix: parts[0],
      timestamp,
      timestamp_human: new Date(timestamp * 1000).toISOString(),
      nonce: parts[2],
      hash: parts[3],
      age_seconds: age,
      is_future: age < 0,
    };
  }

  static generateBatch(count, prefix = 'auth') {
    const batch = [];

    for (let i = 0; i < count; i++) {
      batch.push(this.generate(prefix));
    }

    return batch;
  }
}
